import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Cow, Camera, ArrowLeft, Confetti, CheckCircle, Trophy } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";

// Client-side compress (max 1200px, JPEG q=0.75). Same approach as quiz/profile.
async function compressPhoto(file, maxSide = 1200, quality = 0.75) {
  const url = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = () => rej(new Error("read")); r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("decode")); i.src = url;
  });
  const ratio = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  c.getContext("2d").drawImage(img, 0, 0, w, h);
  return c.toDataURL("image/jpeg", quality);
}

export default function Bingo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isAdminPreview = params.get("preview") === "1" && user?.role === "admin";
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // square being interacted with
  const [showCelebrate, setShowCelebrate] = useState(false);
  const celebrated = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/bingo/card");
        if (!cancelled) setCard(data);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 403) {
          if (!cancelled) setCard(null);
        } else {
          toast.error(formatApiError(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fire celebration once when the user crosses the full-house line.
  useEffect(() => {
    if (card?.full_house && !celebrated.current) {
      celebrated.current = true;
      setShowCelebrate(true);
    }
  }, [card?.full_house]);

  if (loading) {
    return <div className="py-16 text-center text-[#7A828C]">Loading…</div>;
  }
  if (!card) {
    return (
      <div data-testid="bingo-disabled" className="py-12 text-center max-w-md mx-auto">
        <Cow size={48} weight="duotone" className="mx-auto mb-3 text-[#000000]" />
        <h1 className="font-display text-2xl mb-2">Coo Bingo isn&apos;t open yet</h1>
        <p className="text-sm text-[#5a5a5a] mb-4">The DFTB team will switch it on at the conference. Check back soon!</p>
        <Button onClick={() => navigate("/info")} variant="outline" className="rounded-sm">Back to Info</Button>
      </div>
    );
  }

  return (
    <div data-testid="bingo-page" className="max-w-2xl mx-auto px-1 py-2">
      {isAdminPreview && (
        <div data-testid="bingo-admin-preview-banner" className="mb-3 p-3 bg-[#FFF8E5] border border-[#E8D58A] rounded-sm text-xs text-[#5a4a00]">
          <strong className="font-display text-sm text-[#7a5a00]">Admin preview.</strong>{" "}
          You&apos;re seeing the delegate experience. Your card and completions are real (admins are exempt from the off-switch).
        </div>
      )}

      <header className="mb-3 px-2">
        <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] mb-1">Coo Bingo</div>
        <h1 className="font-display text-2xl text-[#1c1c24]">Find folk, snap coos, win DFTB27</h1>
        <p className="text-xs text-[#5a5a5a] mt-1">Complete all 25 for a chance to win a free ticket to DFTB27. Centre square is free - gie it laldy!</p>

        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-[#5a5a5a] mb-1">
            <span data-testid="bingo-progress-label">{card.done_count} / 25 done</span>
            {card.full_house && <span className="text-[#000000] font-mono-time uppercase tracking-widest text-[10px]">Full house!</span>}
          </div>
          <div className="w-full h-2 bg-[#E5E9F0] rounded-sm overflow-hidden">
            <div
              data-testid="bingo-progress-bar"
              className="h-full bg-gradient-to-r from-[#000000] to-[#43C4E3] transition-all"
              style={{ width: `${(card.done_count / 25) * 100}%` }}
            />
          </div>
        </div>
      </header>

      {/* Grid */}
      <div data-testid="bingo-grid" className="grid grid-cols-5 gap-1.5 sm:gap-2 px-1">
        {card.squares.map((sq) => (
          <BingoTile key={sq.position} square={sq} onTap={() => sq.category !== "free" && setActive(sq)} />
        ))}
      </div>

      {/* Action sheet */}
      <BingoActionSheet
        open={!!active}
        square={active}
        onClose={() => setActive(null)}
        onUpdate={(newCard) => { setCard(newCard); setActive(null); }}
      />

      {/* Full-house celebration */}
      <Dialog open={showCelebrate} onOpenChange={setShowCelebrate}>
        <DialogContent className="bg-[#FFFFFF] max-w-md" data-testid="bingo-full-house-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Trophy size={28} weight="duotone" className="text-[#000000]" /> Pure dead brilliant!
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 text-sm text-[#1c1c24] leading-relaxed">
            You&apos;ve got a full house. You&apos;re entered into the draw for a free ticket to <strong>DFTB27</strong>.
            We&apos;ll draw a winner after the conference and message you on the app if you&apos;ve won. <Confetti size={18} className="inline text-[#000000]" />
          </div>
          <Button onClick={() => setShowCelebrate(false)} className="w-full h-11 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="bingo-celebrate-close">
            Brilliant
          </Button>
        </DialogContent>
      </Dialog>

      <div className="px-2 pt-4 pb-6 text-xs text-[#7A828C] text-center">
        Honour system - tap a square when you&apos;ve genuinely done it. Photo squares need a snap.
      </div>
    </div>
  );
}

function BingoTile({ square, onTap }) {
  const done = square.status === "done";
  const isFree = square.category === "free";
  const isPhoto = square.type === "photo";
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={isFree}
      data-testid={`bingo-tile-${square.position}`}
      className={`relative aspect-square p-1.5 sm:p-2 rounded-sm border text-left flex flex-col items-stretch justify-between transition-all ${
        done
          ? isFree
            ? "bg-[#E6F8FC] border-[#000000] text-[#000000] cursor-default"
            : "bg-[#000000] border-[#000000] text-[#FFFFFF]"
          : "bg-[#FFFFFF] border-[#E5E9F0] text-[#1c1c24] hover:border-[#000000]"
      }`}
    >
      <div className="flex items-start justify-between">
        <span className={`font-mono-time text-[8px] uppercase tracking-wider opacity-70`}>
          {isFree ? "Free" : isPhoto ? "Photo" : "Social"}
        </span>
        {isPhoto && !done && <Camera size={11} className="opacity-60 flex-shrink-0" />}
        {square.proof?.photo_url && (
          <img src={square.proof.photo_url} alt="" className="absolute inset-0 w-full h-full object-cover rounded-sm opacity-30 pointer-events-none" />
        )}
      </div>
      <div className={`text-[9px] sm:text-[10px] leading-tight line-clamp-3 relative z-10 ${done ? "font-medium" : ""}`}>
        {square.text}
      </div>
      {done && (
        <div className="absolute bottom-1 right-1 z-10">
          <CheckCircle size={16} weight="fill" className={isFree ? "text-[#000000]" : "text-[#FFFFFF]"} />
        </div>
      )}
    </button>
  );
}

function BingoActionSheet({ open, square, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && square) setNote(square.proof?.note || "");
  }, [open, square]);

  if (!square) return null;
  const isFree = square.category === "free";
  const isPhoto = square.type === "photo";
  const done = square.status === "done";

  const completeSocial = async () => {
    try {
      const { data } = await api.post(`/bingo/card/square/${square.position}/complete`, { note });
      onUpdate(data);
      toast.success("Square ticked!");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const undo = async () => {
    try {
      const { data } = await api.post(`/bingo/card/square/${square.position}/undo`);
      onUpdate(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image"); e.target.value = ""; return;
    }
    setUploading(true);
    try {
      const photo_url = await compressPhoto(file);
      const { data } = await api.post(`/bingo/card/square/${square.position}/photo`, { photo_url });
      onUpdate(data);
      toast.success("Snapped!");
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't upload the photo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#FFFFFF] max-w-md" data-testid="bingo-action-sheet">
        <DialogHeader>
          <DialogTitle className="font-display text-xl pr-6 leading-tight">{square.text}</DialogTitle>
        </DialogHeader>
        <div className="py-1">
          {isFree && (
            <p className="text-sm text-[#5a5a5a]">The centre square is free - it&apos;s already ticked. Enjoy the conference.</p>
          )}
          {!isFree && isPhoto && (
            <div className="space-y-3">
              {square.proof?.photo_url && (
                <img src={square.proof.photo_url} alt="" className="w-full max-h-64 object-contain rounded-sm border border-[#E5E9F0] bg-[#F8F8FB]" />
              )}
              <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhotoPicked} className="hidden" data-testid="bingo-photo-input" />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full h-12 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                data-testid="bingo-photo-upload-btn"
              >
                <Camera size={18} className="mr-2" />
                {uploading ? "Uploading…" : (done ? "Replace photo" : "Take / choose photo")}
              </Button>
            </div>
          )}
          {!isFree && !isPhoto && (
            <div className="space-y-3">
              <div>
                <label className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]">Who did you meet? (optional)</label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Priya from GG&C"
                  className="rounded-sm mt-1 h-11"
                  data-testid="bingo-social-note"
                  maxLength={200}
                />
              </div>
              <Button
                onClick={completeSocial}
                className="w-full h-12 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                data-testid="bingo-social-complete-btn"
              >
                <CheckCircle size={18} className="mr-2" /> Mark done
              </Button>
            </div>
          )}
          {done && !isFree && (
            <Button
              onClick={undo}
              variant="outline"
              className="w-full h-11 rounded-sm mt-3"
              data-testid="bingo-undo-btn"
            >
              <ArrowLeft size={16} className="mr-1" /> Un-tick this square
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
